import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';

import TodayLogo from 'components/TodayLogo';
import { EventEmitter } from 'events';
import Modal from '../../Modal';
import SignupInput from '../SignupInput';

import styles from './styles.module.scss';
import { pickSiteKey } from '../verticalLogic';


/**
 * Generates success text for newsletter signup.
 *
 * @param {string} email - The email address that was successfully subscribed.
 * @returns {object} Object containing title and description for success message.
 */
const signupSuccessText = (email) => ({
  title: 'You have successfully signed up.',
  // eslint-disable-next-line max-len
  description: `${email} has subscribed to the newsletter. Check your inbox for a confirmation email.`,
});


/**
 * Returns the appropriate icon component based on the vertical.
 *
 * @param {string} vertical - The vertical identifier (e.g., 'today', 'news').
 * @returns {JSX.Element} The icon component for the specified vertical.
 */
const getIcon = (vertical) => {
  if (vertical === 'today') {
    return <TodayLogo textColor="black" sunriseColor="#FF503C" />;
  }

  const iconClasses = classNames('icon', {
    'icon-nbcnews-white': vertical === 'news',
  });

  return <span className={iconClasses} />;
};

/**
 * Maps Redux state to component props.
 *
 * @param {object} state - The Redux state object.
 * @param {object} state.shared - Shared state containing global app data.
 * @param {object} ownProps - The component's own props.
 * @param {string} ownProps.vertical - The vertical passed as a prop.
 * @returns {object} The mapped props object.
 */
const mapStateToProps = ({ shared }, { vertical }) => ({
  vertical: vertical || shared.vertical,
});

/**
 * PopupModule component for displaying newsletter signup in a modal popup.
 * This component manages the display state, handles cookie-based signup tracking,
 * and provides a modal interface for newsletter subscriptions.
 *
 * @param {object} props - The component props.
 * @param {string} [props.vertical='news'] - The vertical identifier for styling and behavior.
 * @param {object} props.cookies - The cookies object for managing signup state persistence.
 * @returns {JSX.Element|null} The popup modal component or null if not shown.
 */
const PopupModule = ({ vertical = 'news', cookies }) => {
  const [hasSignedUp, setHasSignedUp] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [moduleType, setModuleType] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [hasReceivedSignup, setHasReceivedSignup] = useState(false);

  useEffect(() => {
    const setModuleEvent = new EventEmitter();
    const cookieValue = true;
    const numSecondsInDay = 86400;
    let cookieName;

    setModuleEvent.on('setModule', (moduleTypeData) => {
      setModuleType(moduleTypeData);
      cookieName = moduleTypeData.newsletterId;
      if (cookies && !cookies.get(cookieName)) {
        cookies.set(cookieName, cookieValue, { maxAge: numSecondsInDay * 7 });
      } else {
        setHasReceivedSignup(true);
      }
    });
    window.setModuleEvent = setModuleEvent;

    return () => {
      // Cleanup: remove the event listener when component unmounts
      setModuleEvent.removeAllListeners('setModule');
      if (window.setModuleEvent === setModuleEvent) {
        delete window.setModuleEvent;
      }
    };
  }, [cookies]);


  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback((email) => {
    setHasSignedUp(true);
    setUserEmail(email);
    document.dispatchEvent(new Event('emailSignup'));
  }, []);

  const moduleInfo = hasSignedUp ? signupSuccessText(userEmail) : moduleType;
  const siteKey = pickSiteKey(vertical);
  const defaultSignupSource = 'popup-mod';

  return moduleType && !hasReceivedSignup ? (
    <Modal>
      <div
        id="email-popup-module"
        className={classNames(
          'fixed h-100 w-100 top0 left0 justify-center items-center df',
          styles.opaqueBackground,
          { 'v-hidden': !isOpen },
        )}
        data-testid="newsletter-popup-module"
      >
        <div className={classNames('relative', styles.container)}>
          <div className={classNames('relative', styles.content)}>
            <button
              className="absolute top1 right1 f7 b-none bg-white"
              onClick={closeModal}
              type="button"
            >
              &#10005;
            </button>
            {getIcon(vertical)}
            <h2 className="f8 mv3">{moduleInfo.title}</h2>
            <h4 className="publico-txt fw1 lh-copy mt0 mb4">{moduleInfo.description}</h4>
            <SignupInput
              newsletterId={moduleType.newsletterId}
              onSubmit={onSubmit}
              signupSource={moduleType.signupSource || defaultSignupSource}
              sitekey={siteKey}
              vertical={vertical}
            />
            <section className={classNames('absolute', styles.dots)} />
          </div>
        </div>
      </div>
    </Modal>
  ) : null;
};

export const PopupModuleConnected = connect(mapStateToProps)(withCookies(PopupModule));

